import classnames from 'classnames';
import * as React from 'react';
import { memo, useCallback } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { Action } from 'redux-actions';
import MediaQuery from 'react-responsive';

import { Segment as SegmentModel } from '../../../../schemas/Segment';
import { SearchFormState } from '../../../../store/reducers';
import {
	clearDates,
	OnDateChangeHandler,
	OnDateMultiChangeHandler,
	OnRemoveSegmentHandler,
	OnSetLocationHandler,
	removeSegment,
	setDate,
	SetDateAction,
	setDateMultiCity,
	setLocation,
	SetLocationAction,
	swapLocations,
	swapMultiLocations,
	addSegment
} from '../../../../store/segments/actions';
import {
	canAddSegment,
	canRemoveSegment,
	getSelectedDates,
	isMultiCity,
	makeGetSegment
} from '../../../../store/segments/selectors';
import { LocationType } from '../../../../../types';
import Datepicker from './Datepicker/Datepicker';
import LocationField from './Location/Location';
import { Location } from '../../../../../LocationAutocomplete/types';
import { addRecentSearch, AddRecentSearchAction } from '../../../../store/recentSearches/actions';
import { getRecentSearches } from '../../../../store/recentSearches/selectors';
import { useConfig } from '../../../../../context';
import { useTheme } from '../../../../../theme';
import { getPassengersForRequest } from '../../../../store/passengers/selectors';
import { PassengerTypeSearchParameter } from '@websky/graphql';
import { MOBILE_MIN_WIDTH } from '../../../../../utils';
import { useRenderers } from '../../../../../renderProps';
import { CdnIcon } from '@shared/ui';

interface OwnProps {
	segmentId: number;
}

interface StateProps {
	selectedDates: Date[];
	isMultiCity: boolean;
	canAddSegment: boolean;
	canRemoveSegment: boolean;
	segment: SegmentModel;
	recentSearches: Location[];
	passengers: PassengerTypeSearchParameter[];
}

interface DispatchProps {
	addSegment: () => Action<null>;
	clearDates: () => Action<null>;
	swapLocations: () => Action<null>;
	swapMultiLocations: (segmentIndex: number) => Action<null>;
	addRecentSearch: (location: Location) => AddRecentSearchAction;
	removeSegment: OnRemoveSegmentHandler;
	setDate: OnDateChangeHandler<SetDateAction>;
	setDateMultiCity: OnDateMultiChangeHandler;
	setLocation: OnSetLocationHandler<SetLocationAction>;
}

type Props = OwnProps & StateProps & DispatchProps;

const datesAreNotEqual = (prevDates: Date[], nextDates: Date[]): boolean => {
	// Quick check.
	if (prevDates.length !== nextDates.length) {
		return true;
	}

	// Slow check.
	const numOfDates = prevDates.length;

	for (let i = 0; i < numOfDates; i++) {
		if (prevDates[i] !== nextDates[i]) {
			return true;
		}
	}

	return false;
};

const Segment = memo<Props>(
	({
		isMultiCity,
		recentSearches,
		setDate,
		setDateMultiCity,
		swapLocations,
		swapMultiLocations,
		setLocation,
		segmentId,
		segment,
		selectedDates,
		clearDates,
		addRecentSearch,
		removeSegment,
		canAddSegment,
		canRemoveSegment,
		passengers,
		addSegment
	}) => {
		const { Segment: css } = useTheme('SearchForm');

		const { proMode, showPricesInDatepicker, mobileFormType } = useConfig().SearchForm;
		const alternative = mobileFormType !== 'common';
		const isSegmentFirst = segmentId === 0;
		const isControlDisabled = isSegmentFirst ? !canAddSegment : !canRemoveSegment;

		const {
			SearchFormLocation: SearchFormLocationOverride = LocationField,
			SearchFormSegmentDatepicker: SearchFormSegmentDatepickerOverride = Datepicker
		} = useRenderers();

		const onRemoveSegment = useCallback(() => removeSegment(segmentId), [segmentId]);

		const onDateChange = useCallback(
			(date: Date, isBackDate = false) => {
				if (isMultiCity) {
					setDateMultiCity(date, segmentId);
				} else {
					setDate(date, isBackDate);

					if (!isBackDate) {
						// Reset back date.
						setDate(null, true);
					}
				}
			},
			[segmentId, isMultiCity]
		);

		const onSetLocation = useCallback(
			(location: Location, type: LocationType) => {
				if (type === LocationType.Departure && location.iata === (segment.arrival && segment.arrival.iata)) {
					setLocation(null, LocationType.Arrival);
				}

				if (isMultiCity) {
					setLocation(location, type, segmentId);
				} else {
					setLocation(location, type);
				}

				addRecentSearch(location);
			},
			[segmentId, isMultiCity, segment]
		);

		const onSwapHandler = useCallback(() => {
			if (isMultiCity) {
				swapMultiLocations(segmentId);
			} else {
				swapLocations();
			}
		}, [isMultiCity, segmentId, swapLocations, swapMultiLocations]);

		return (
			<div
				className={classnames(css.segment, { [css.alternative]: alternative, [css.segment_advanced]: proMode })}
			>
				<div className={classnames(css.wrapper, { [css.withMultiCity]: isMultiCity })}>
					<div className={`${css.cell} ${css.cell_location}`}>
						<div className={css.departureInputWrapper}>
							<SearchFormLocationOverride
								type={LocationType.Departure}
								segmentId={segmentId}
								locations={{
									departure: segment.departure,
									arrival: segment.arrival
								}}
								setLocation={onSetLocation}
								onSwap={onSwapHandler}
								recentSearches={recentSearches}
								withMultiCity={isMultiCity}
							/>
						</div>
					</div>

					<div className={`${css.cell} ${css.cell_location} ${css.cell_location_arrival}`}>
						<SearchFormLocationOverride
							type={LocationType.Arrival}
							segmentId={segmentId}
							locations={{
								departure: segment.departure,
								arrival: segment.arrival
							}}
							setLocation={onSetLocation}
							recentSearches={recentSearches}
							withMultiCity={isMultiCity}
						/>
					</div>

					<div
						className={classnames(css.cell, css.cell_date, {
							[css.cell_fixed]: isMultiCity
						})}
					>
						<SearchFormSegmentDatepickerOverride
							valueClassName={css.datepicker_value}
							passengers={passengers}
							dateTo={segment.date}
							dateBack={segment.dateBack}
							selectedDates={selectedDates}
							onDateChange={onDateChange}
							withDateBack={!isMultiCity}
							showPrices={showPricesInDatepicker}
							clearDates={clearDates}
							segmentId={segmentId}
							locations={{
								departure: segment.departure,
								arrival: segment.arrival
							}}
						/>
					</div>
				</div>

				{isMultiCity && (
					<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
						<div
							onClick={isSegmentFirst && canAddSegment ? addSegment : onRemoveSegment}
							className={classnames(css.control, {
								[css.control_disabled]: isControlDisabled
							})}
						>
							<CdnIcon icon={isSegmentFirst ? 'Action/Add' : 'Action/Decr'} />
						</div>
					</MediaQuery>
				)}
			</div>
		);
	},
	(prevProps, nextProps) =>
		prevProps.segmentId === nextProps.segmentId &&
		prevProps.segment === nextProps.segment &&
		!datesAreNotEqual(prevProps.selectedDates, nextProps.selectedDates) &&
		prevProps.isMultiCity === nextProps.isMultiCity &&
		prevProps.canRemoveSegment === nextProps.canRemoveSegment &&
		prevProps.canAddSegment === nextProps.canAddSegment
);

const mapStateToProps = (
	state: SearchFormState,
	ownProps: OwnProps
): MapStateToProps<StateProps, OwnProps, SearchFormState> => {
	const getSegment = makeGetSegment(ownProps.segmentId);

	return (state, ownProps) => {
		return {
			...ownProps,
			selectedDates: getSelectedDates(state),
			isMultiCity: isMultiCity(state),
			canAddSegment: canAddSegment(state),
			canRemoveSegment: canRemoveSegment(state),
			segment: getSegment(state),
			recentSearches: getRecentSearches(state),
			passengers: getPassengersForRequest(state)
		};
	};
};

const mapDispatchToProps: DispatchProps = {
	removeSegment,
	setDate,
	setDateMultiCity,
	clearDates,
	setLocation,
	swapLocations,
	swapMultiLocations,
	addRecentSearch,
	addSegment
};

export default connect(mapStateToProps, mapDispatchToProps)(Segment);
